import { useEffect, useState, useCallback } from 'react';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { handleActionPlanErrorWithFriendlyMessage, ACTION_PLAN_API_NAMES } from '../../../store/utils/actionPlanErrorHandler';

export interface AuditHistoryItem {
  ID: number;
  ACTION_PLAN_ID: number;
  ACTION_TYPE: 'CREATED' | 'UPDATED' | 'DECLINED' | 'ACCEPTED';
  PERFORMED_BY_GPID: string;
  PERFORMED_BY_NAME?: string;
  PERFORMED_BY_EMAIL?: string;
  PREVIOUS_VALUES: string | null;
  NEW_VALUES: string;
  NOTES: string | null;
  TIMESTAMP: string;
  QUESTION_ID?: number;
  QUESTION_TITLE?: string;
}

export interface AuditHistoryResponse {
  status: string;
  message: string;
  data: AuditHistoryItem[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    pageSize: number;
  };
}

interface UseAuditHistoryProps {
  actionPlanId: string | number;
  refreshKey?: number;
  isTemp?: boolean | number;
}

export const useAuditHistory = ({ actionPlanId, refreshKey, isTemp }: UseAuditHistoryProps) => {
  const [auditHistory, setAuditHistory] = useState<AuditHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionTitle, setQuestionTitle] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    pageSize: 100,
  });

  const fetchAuditHistory = useCallback(
    async (pageNumber: number = 1) => {
      if (!actionPlanId) {
        setError('Action Plan ID is required');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const api = createApiClient();
        let url = `/action-plans/${actionPlanId}/history?pageNumber=${pageNumber}&pageSize=100`;
        
        // Add is_temp parameter if the action plan is temporary
        if (isTemp === true || isTemp === 1) {
          url += '&is_temp=true';
        }
        console.log('Fetching audit history from:', url);

        const response = (await apiClient(api, url, 'GET')) as AuditHistoryResponse;

        console.log('Audit history response:', response);

        if (response && response.data) {
          setAuditHistory(response.data);
          if (response.pagination) {
            setPagination(response.pagination);
          }
          
          // Extract QUESTION_TITLE from first history record for Area mapping
          console.log('[useAuditHistory] Checking for QUESTION_TITLE in history data:', {
            dataLength: response.data.length,
            firstRecord: response.data[0],
            'firstRecord.QUESTION_TITLE': response.data[0]?.QUESTION_TITLE,
          });
          
          if (response.data.length > 0 && response.data[0].QUESTION_TITLE) {
            console.log('[useAuditHistory] ✅ Found QUESTION_TITLE:', response.data[0].QUESTION_TITLE);
            setQuestionTitle(response.data[0].QUESTION_TITLE);
          } else {
            console.log('[useAuditHistory] ⚠️ No QUESTION_TITLE found in first history record');
          }
        }
      } catch (err: any) {
        console.error('Error fetching audit history:', err);

        let errorMessage = 'Failed to load audit history';

        if (err.response?.status === 400) {
          errorMessage = 'Invalid Action Plan ID';
        } else if (err.response?.status === 401) {
          errorMessage = 'Unauthorized to view audit history';
        } else if (err.response?.status === 404) {
          errorMessage = 'Action plan not found';
        } else if (err.response?.status === 500) {
          errorMessage = 'Server error while loading audit history';
        } else if (err.message) {
          errorMessage = err.message;
        }

        // Dispatch centralized ActionPlan error and show modal
        try {
          const centralizedError = handleActionPlanErrorWithFriendlyMessage(ACTION_PLAN_API_NAMES.FETCH_HISTORY, err, true, { actionPlanId, pageNumber });
          setError(centralizedError?.message || errorMessage);
        } catch (handlerErr) {
          console.error('Failed to dispatch centralized ActionPlan error for audit history:', handlerErr);
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    },
    [actionPlanId]
  );

  useEffect(() => {
    fetchAuditHistory();
  }, [fetchAuditHistory, refreshKey]);

  const retryFetch = useCallback(() => {
    fetchAuditHistory(pagination.currentPage);
  }, [fetchAuditHistory, pagination.currentPage]);

  return {
    auditHistory,
    loading,
    error,
    pagination,
    refetch: retryFetch,
    questionTitle,
  };
};
